import {
  useEffect,
  useMemo,
  useState,
  MouseEvent as ReactMouseEvent,
} from "react"

import { ColumnInstance, IdType } from "react-table"
import { DragDropList, ListItem } from "../DragDropList"
import {
  box,
  column,
  headerContainer,
  item,
  margin,
  popover,
  row,
  subheader,
} from "./styles"

import Button from "ui/buttons/Button"
import Checkbox from "@material-ui/core/Checkbox"
import CloseIcon from "@material-ui/icons/Close"
import { ReactComponent as ColumnsIcon } from "assets/icon-columns.svg"
import IconButton from "ui/buttons/IconButton"
import Popover from "@material-ui/core/Popover"
import { cx } from "@emotion/css"
import fromPairs from "lodash/fp/fromPairs"
import groupBy from "lodash/fp/groupBy"
import prop from "lodash/fp/prop"
import { useI18n } from "providers/I18n"

type ColumnsProps<T extends object = {}> = {
  data: Array<ColumnInstance<T> & { header: string; fixed: boolean }>
  onHiddenChange?: (hiddenColumns: Array<IdType<T>>) => void
  onOrderChange: (columnOrder: Array<IdType<T>>) => void
  variant?: "columns" | "customize"
  hasReordering?: boolean
  hasHiddenCols?: boolean
}

function Columns<T extends object = {}>({
  data,
  onOrderChange,
  onHiddenChange,
  hasReordering = true,
  hasHiddenCols = true,
}: ColumnsProps<T>) {
  const { t } = useI18n()
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null)
  const [checked, setChecked] = useState<Record<string, boolean>>({})
  const [all, setAll] = useState<Array<ListItem<T>>>([])

  useEffect(() => {
    const newColumns = data.map((c) => ({
      ...c, // keep original column data
      content: c.header || c.id,
      fixed: !!c.fixed || !!c.placeholderOf?.fixed,
      order: 0,
    }))
    setAll(newColumns)
  }, [data])

  const byMediaType = useMemo(() => {
    const grouped = groupBy(
      prop("mediaType"),
      all.filter((c: any) => c.mediaType !== undefined),
    )
    setChecked(
      fromPairs(
        Object.keys(grouped).map((mt) => [
          mt,
          !(grouped[mt].find((c) => c.isVisible) === undefined),
        ]),
      ),
    )
    return grouped
    // eslint-disable-next-line
  }, [all.length])

  const onOpen = (event: ReactMouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const onClose = () => {
    setAnchorEl(null)
  }

  const open = Boolean(anchorEl)

  const handleReorder = (newItems: Array<ListItem<T>>) => {
    onOrderChange(newItems.map((i) => i.id))
  }

  const handleMediaTypeChange = (mediaType: string) => {
    setChecked({ ...checked, [mediaType]: !checked[mediaType] })
  }

  return (
    <div>
      <Button
        onClick={onOpen}
        weight="normal"
        startIcon={<ColumnsIcon />}
        className={cx({ active: open })}
      >
        {t("customize.columns.button")}
      </Button>
      <Popover
        open={open}
        onClose={onClose}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
        classes={{ paper: popover }}
      >
        <div className={box}>
          <div className={headerContainer}>
            <div className="BodyBody-1BlackBold">
              {t("customize.columns.title")}
            </div>
            <IconButton aria-label="close" onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </div>
          <div className={row}>
            <div className={column}>
              {Object.keys(byMediaType).length > 0 ? (
                <>
                  <h5 className={cx("BodyBody-2BlackLight", subheader)}>
                    {t("customize.columns.mediaType")}
                  </h5>
                  {Object.keys(byMediaType).map((mt) => (
                    <div className={item} key={mt}>
                      <Checkbox
                        color="primary"
                        checked={checked[mt]}
                        onChange={() => handleMediaTypeChange(mt)}
                      />
                      <span className="BodyBody-2BlackRegular">{mt}</span>
                    </div>
                  ))}
                  <div className={margin} />
                </>
              ) : null}
              <h5 className={cx("BodyBody-2BlackLight", subheader)}>
                {t("customize.columns.all")}
              </h5>
              {all.map((i: any) => {
                const { onChange, ...rest } = i.getToggleHiddenProps()
                return i.mediaType === undefined || checked[i.mediaType] ? (
                  <div className={item} key={i.id}>
                    <Checkbox
                      data-testid={t(i.content)}
                      {...rest}
                      disabled={i.fixed || !hasHiddenCols}
                      color="primary"
                      onChange={(args) => {
                        onHiddenChange?.(i.id)
                        onChange(args)
                      }}
                    />
                    <span className="BodyBody-2BlackRegular">
                      {t(i.content)}
                    </span>
                  </div>
                ) : null
              })}
            </div>
            {hasReordering && (
              <div className={column}>
                <h5 className={cx("BodyBody-2BlackLight", subheader)}>
                  {t("customize.columns.selected")}
                </h5>
                <DragDropList
                  data={all}
                  onChange={handleReorder}
                  hasHiddenCols={hasHiddenCols}
                />
              </div>
            )}
          </div>
        </div>
      </Popover>
    </div>
  )
}

export { Columns }
